'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Building2, CheckCircle2, MapPin, ArrowRight, Zap, ShieldCheck, Calendar, Filter, Sparkles } from 'lucide-react';

export default function ProjectsPage() {
  const [activeFilter, setActiveFilter] = useState<string>('ALL');

  const projects = [
    {
      title: 'Kompleks Vilash Rezidenciale — Lundër, Tiranë',
      type: 'HEATPUMP',
      category: 'Pompa Nxehtësie & Ngrohje Dyshemeje',
      city: 'Tiranë',
      brand: 'Daikin Altherma 3',
      power: '6x 16 kW',
      duration: '14 ditë',
      description: 'Instalim i 6 pompave nxehtësie Daikin Altherma 3 ajër-ujë me kolektorë Caleffi dhe sistem ngrohje-ftohje nën dysheme me kontroll individual dixhital për çdo dhomë.',
      year: '2025',
    },
    {
      title: 'Qendër Biznesi & Zyra — Elbasan',
      type: 'COMMERCIAL',
      category: 'Sistem VRV Daikin 48 HP',
      city: 'Elbasan',
      brand: 'Daikin VRV IV+',
      power: '48 HP (135 kW)',
      duration: '21 ditë',
      description: 'Sistem qendror VRV me rikuperim nxehtësie, 24 njësi të brendshme kasete me kontroll të centralizuar inteligjent dhe faturim individual të konsumit të energjisë.',
      year: '2024',
    },
    {
      title: 'Hotel & Spa Bregdetar — Spille / Durrës',
      type: 'SOLAR',
      category: 'Ujë Sanitar Diellor & Multi-Split',
      city: 'Durrës',
      brand: 'Sonnenkraft & Gree',
      power: '18 Panele / 3000L',
      duration: '10 ditë',
      description: 'Impiant me 18 kolektorë diellorë me qarkullim të detyruar dhe bojler akumulimi 3,000 litra së bashku me sistem klimatizimi Multi-Split me efiçiencë A+++.',
      year: '2024',
    },
    {
      title: 'Linjë Prodhimi Farmaceutike — Zona Industriale Elbasan',
      type: 'COMMERCIAL',
      category: 'Klimatizim Industrial me Filtra HEPA',
      city: 'Elbasan',
      brand: 'Carrier & Daikin',
      power: '60 kW Çiller',
      duration: '30 ditë',
      description: 'Trajtim ajri me presion pozitiv dhe temperaturë të kontrolluar me saktësi ±0.5°C sipas standardeve më strikte ndërkombëtare GMP.',
      year: '2023',
    },
    {
      title: 'Vilë Private 3-Katëshe — Rolling Hills, Tiranë',
      type: 'HEATPUMP',
      category: 'Pompë Nxehtësie & Fancoil me Kanal',
      city: 'Tiranë',
      brand: 'Mitsubishi Zubadan',
      power: '22 kW',
      duration: '8 ditë',
      description: 'Sistem hibrid me pompë nxehtësie me injeksion flash që garanton ngrohje 100% konstante edhe në temperatura ekstreme deri në -25°C.',
      year: '2025',
    },
    {
      title: 'Agroturizëm & Restorant — Belsh, Dumre',
      type: 'SOLAR',
      category: 'Kombinim Diellor & Kondicionim Inverter',
      city: 'Belsh',
      brand: 'Midea & Elbi',
      power: '8 Kolektorë / 1000L',
      duration: '6 ditë',
      description: 'Zgjidhje eko-miqësore me prodhim të ujit sanitar nga dielli dhe 6 kondicionerë kasete 360° për ambientet e restorantit.',
      year: '2024',
    },
  ];

  const filteredProjects = activeFilter === 'ALL' ? projects : projects.filter((p) => p.type === activeFilter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 w-full space-y-10">
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-sky-50 dark:bg-sky-950/60 text-sky-600 dark:text-sky-400 text-xs font-bold border border-sky-100 dark:border-sky-900">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Eksperiencë në Terren & Referenca Reale</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
          Portofoli i Projekteve të Realizuara
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
          Mbi 2,500 instalime të suksesshme në vila, qendra biznesi, hotele dhe industri në të gjithë Shqipërinë.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex justify-center gap-2 flex-wrap">
        {[
          { key: 'ALL', label: 'Të Gjitha Projektet' },
          { key: 'HEATPUMP', label: 'Pompa Nxehtësie & Dysheme' },
          { key: 'COMMERCIAL', label: 'Sisteme VRV & Biznese' },
          { key: 'SOLAR', label: 'Energji Diellore & Bojlera' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all shadow-sm ${
              activeFilter === tab.key
                ? 'bg-sky-600 text-white shadow-sky-600/25 scale-105'
                : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Project Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProjects.map((p, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 p-6 sm:p-7 flex flex-col justify-between hover:border-sky-300 dark:hover:border-sky-600 hover:shadow-xl transition-all group"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="px-3 py-1 rounded-full bg-sky-50 dark:bg-sky-950/70 text-sky-700 dark:text-sky-300 font-extrabold border border-sky-100 dark:border-sky-900 text-[11px]">
                  {p.category}
                </span>
                <span className="text-slate-400 font-bold font-mono">{p.year}</span>
              </div>

              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                {p.title}
              </h2>

              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                {p.description}
              </p>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-2 pt-2 text-[11px]">
                <div className="bg-slate-50 dark:bg-slate-800/80 p-2 rounded-xl border border-slate-200/60 dark:border-slate-700">
                  <span className="text-slate-400 block text-[10px]">Pajisja:</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">{p.brand}</span>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/80 p-2 rounded-xl border border-slate-200/60 dark:border-slate-700">
                  <span className="text-slate-400 block text-[10px]">Kapaciteti:</span>
                  <span className="font-bold text-sky-600 dark:text-sky-400">{p.power}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 pt-1">
                <MapPin className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 flex-shrink-0" />
                <span>{p.city}, Shqipëri</span>
              </div>
            </div>

            <div className="pt-5 mt-5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Përfunduar
              </span>
              <Link
                href="/request-quote"
                className="text-xs font-bold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 flex items-center gap-1"
              >
                Kërko Zgjidhje &rarr;
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
